/**
 * This hooks allows to retrieve the different documentation strings present in a
 * TemplateField: Model Field Documentation, Model Field Type Documentation, and
 * Configuration Field Documentation
 */
import type { Optional } from '@festina-life/core'
import { useMemo } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { typeOptions } from '../../context/typeOptions'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import {
  isSubTemplatedField,
  type TemplateField,
  ValueSetupRole,
} from '../../types/templateModel'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import { subTemplatedFieldValueDocumentation } from './helpers'

export const useFieldDocumentation = (field: TemplateField) => {
  const { templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)
  const { applicationEnumDefinitions } = useApplicationViewOperationsContext()

  const inScopeVariant =
    isSubTemplatedField(field) &&
    field.role === ValueSetupRole.SINGLE_CLASS_SUB_TEMPLATE
      ? field.value?.variants.at(0)
      : undefined

  const variantDocumentation = useMemo(
    () =>
      inScopeVariant
        ? subTemplatedFieldValueDocumentation(inScopeVariant)
        : null,
    [inScopeVariant],
  )

  const modelFieldDocumentation =
    field.modelPropertyDescriptor?.documentation ??
    field.templatePropertyDescriptor?.documentation

  const modelFieldType =
    field.originalPropertyType ?? field.modelPropertyDescriptor
  const values = useMemo(
    () =>
      typeOptions({
        typeName:
          field.templatePropertyDescriptor?.onlyTemplatedTypeName ??
          modelFieldType?.typeName,
        metaModel,
        configuredEnums: applicationEnumDefinitions,
      }),
    [
      applicationEnumDefinitions,
      field.templatePropertyDescriptor?.onlyTemplatedTypeName,
      metaModel,
      modelFieldType?.typeName,
    ],
  )

  const modelFieldTypeDocumentation = modelFieldType?.documentation

  const configurationFieldDocumentation = field.value?.documentation
  const configurationFieldDocumentationPath =
    variantDocumentation?.configurationFieldDocumentation
      ? variantDocumentation?.configurationFieldDocumentationPath
      : `${field.path}.documentation`

  const modelFieldTypeName: Optional<string> =
    modelFieldType?.typeName ??
    field.templatePropertyDescriptor?.onlyTemplatedTypeName ??
    field.templatePropertyDescriptor?.typeName

  return {
    values,
    modelFieldType:
      variantDocumentation?.modelFieldType ?? modelFieldTypeName ?? '-',
    modelFieldDocumentation,
    modelFieldTypeDocumentation:
      variantDocumentation?.modelFieldTypeDocumentation ??
      modelFieldTypeDocumentation,
    configurationFieldDocumentation:
      variantDocumentation?.configurationFieldDocumentation ??
      configurationFieldDocumentation,
    onlyTemplated: field.templatePropertyDescriptor?.stereoTypes?.includes(
      'OnlyTemplatedTemplateProp',
    ),
    configurationFieldDocumentationPath,
  }
}
